import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatUsuarioLabel } from '@leadsphere/types';

import { Badge, type BadgeTone } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { Input } from '../../components/ui/Input.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';
import { fmtFechaDisplay } from '../../lib/dates.js';
import type { Usuario } from '../clientes/types.js';
import type { EstadoSolicitud, ResultadoDiasHabiles, ResultadoSaldo, VacacionesApplication } from './types.js';

const ROLES_RH = ['root', 'head'];

const ESTADO_INFO: Record<EstadoSolicitud, { label: string; tone: BadgeTone }> = {
  pendiente:  { label: 'Pendiente',  tone: 'warning' },
  aprobada:   { label: 'Aprobada',   tone: 'success' },
  rechazada:  { label: 'Rechazada',  tone: 'danger' },
  cancelada:  { label: 'Cancelada',  tone: 'neutral' },
};

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

// Prompt AF(RH) — pantalla "Ausencias", visible a todos (sidebar nivel 1,
// fuera de Gestión/RH): saldo disponible + formulario de nueva solicitud +
// historial propio, todo en una sola vista (a diferencia del patrón
// lista+detalle de Días Festivos/Tipos de Ausencia — aquí es un dashboard
// personal, no un catálogo administrable).
export function AusenciasPage() {
  const { user } = useAuth();
  const usuarioId = user!.sub;
  const esRH = ROLES_RH.includes(user!.rol);
  const { get, post, patch } = useApi();
  const queryClient = useQueryClient();
  const { toasts, showToast, dismiss } = useToasts();

  const [desgloseAbierto, setDesgloseAbierto] = useState(false);
  const [categoriaVisible, setCategoriaVisible] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [motivo, setMotivo] = useState('');
  const [aCancelar, setACancelar] = useState<VacacionesApplication | null>(null);
  // Prompt AF-3(RH) — "Solicitar a nombre de": '' = para mí mismo (flujo
  // preexistente, sin cambios). Solo root/head ven este selector.
  const [usuarioObjetivoId, setUsuarioObjetivoId] = useState('');
  const [aprobarInmediato, setAprobarInmediato] = useState(false);

  const esParaOtro = usuarioObjetivoId !== '' && usuarioObjetivoId !== usuarioId;
  // Prompt BB-2(RH) — bug real corregido: el saldo mostrado (dashboard +
  // selector) se congelaba en "el mío" sin importar a quién se eligiera en
  // "Solicitar a nombre de" (mostraba 0 días para Sebastian Barrios en vez
  // de sus 44 reales). El titular EFECTIVO de la solicitud es quien debe
  // determinar qué saldo se consulta y se muestra — una sola fuente de
  // verdad, no dos queries separadas que solo una de ellas se pintaba.
  const usuarioEfectivoId = esParaOtro ? usuarioObjetivoId : usuarioId;

  const { data: saldo, isLoading: cargandoSaldo } = useQuery({
    queryKey: ['vacaciones-saldo', usuarioEfectivoId],
    queryFn: () => get<ResultadoSaldo>(`/vacaciones-allocations/saldo/${usuarioEfectivoId}`),
  });

  const { data: usuarios } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => get<Usuario[]>('/usuarios'),
    enabled: esRH,
  });

  // Prompt BB-5(RH) — bug real corregido, misma clase que el saldo (BB-2): el
  // historial se quedaba anclado al usuarioId del ACTOR, sin importar a quién
  // se eligiera en "Solicitar a nombre de" — por eso el historial de
  // Sebastian Barrios (con solicitudes reales) aparecía vacío cuando lo veía
  // root desde ahí. usuarioEfectivoId ya resuelve esto para el saldo; el
  // historial debe seguir la misma fuente de verdad.
  const { data: historial, isLoading: cargandoHistorial } = useQuery({
    queryKey: ['vacaciones-applications', usuarioEfectivoId],
    queryFn: () => get<VacacionesApplication[]>(`/vacaciones-applications?usuarioId=${usuarioEfectivoId}`),
  });

  // Prompt BB-2(RH) — selector agrupado por categoriaVisible: ya no muestra
  // tramos individuales ("Aniversario 5"/"Aniversario 6-10" por separado),
  // solo la categoría visible aplicable ("Vacaciones" aparece si hay al
  // menos 1 allocation vigente de antigüedad; "Enfermedad" solo si RH ya le
  // dio de alta esa allocation puntual — ambos casos ya quedan cubiertos
  // solo con derivar del desglose de saldo real, sin lista genérica aparte).
  const categoriasDisponibles = useMemo(() => {
    if (!saldo) return [];
    return [...new Set(saldo.desglose.map(d => d.categoriaVisible))];
  }, [saldo]);

  const { data: preview } = useQuery({
    queryKey: ['dias-habiles-preview', fechaInicio, fechaFin],
    queryFn: () => get<ResultadoDiasHabiles>(`/dias-festivos-listas/calcular-dias-habiles?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`),
    enabled: Boolean(fechaInicio && fechaFin && fechaInicio <= fechaFin),
  });

  const crearMutation = useMutation({
    mutationFn: () =>
      post<VacacionesApplication>('/vacaciones-applications', {
        categoria_visible: categoriaVisible,
        fecha_inicio_solicitada: fechaInicio,
        fecha_fin_solicitada: fechaFin,
        motivo: motivo.trim() || undefined,
        usuario_id: esParaOtro ? usuarioObjetivoId : undefined,
        aprobar_inmediato: esParaOtro && aprobarInmediato ? true : undefined,
      }),
    onSuccess: creada => {
      queryClient.invalidateQueries({ queryKey: ['vacaciones-applications', usuarioEfectivoId] });
      queryClient.invalidateQueries({ queryKey: ['vacaciones-saldo', usuarioEfectivoId] });
      setCategoriaVisible('');
      setFechaInicio('');
      setFechaFin('');
      setMotivo('');
      setUsuarioObjetivoId('');
      setAprobarInmediato(false);
      showToast('success', creada.estado === 'aprobada' && esParaOtro ? 'Solicitud creada y aprobada correctamente' : 'Solicitud enviada correctamente');
    },
    onError: err => showToast('error', errorMessage(err, 'No fue posible enviar la solicitud')),
  });

  const cancelarMutation = useMutation({
    mutationFn: (id: string) => patch<VacacionesApplication>(`/vacaciones-applications/${id}/cancelar`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacaciones-applications', usuarioEfectivoId] });
      queryClient.invalidateQueries({ queryKey: ['vacaciones-saldo', usuarioEfectivoId] });
      showToast('success', 'Solicitud cancelada');
      setACancelar(null);
    },
    onError: err => {
      showToast('error', errorMessage(err, 'No fue posible cancelar la solicitud'));
      setACancelar(null);
    },
  });

  function handleEnviar() {
    if (!categoriaVisible) {
      showToast('error', 'Elige un tipo de ausencia');
      return;
    }
    if (!fechaInicio || !fechaFin) {
      showToast('error', 'Elige el rango de fechas');
      return;
    }
    if (fechaInicio > fechaFin) {
      showToast('error', 'La fecha de inicio no puede ser posterior a la fecha de fin');
      return;
    }
    crearMutation.mutate();
  }

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Ausencias</div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>
          {esParaOtro ? `Solicitando a nombre de ${usuarios?.find(u => u.id === usuarioObjetivoId)?.nombre ?? '…'}` : 'Solicita días y consulta tu saldo disponible'}
        </div>
      </div>

      {/* ── Saldo ─────────────────────────────────────────────────────────── */}
      <div className="plat-card" style={{ maxWidth: 640, marginBottom: 20 }}>
        {cargandoSaldo ? (
          <div>Cargando saldo…</div>
        ) : (
          <>
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              onClick={() => setDesgloseAbierto(v => !v)}
            >
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--heading)' }}>
                {saldo?.totalDisponible ?? 0} día{saldo?.totalDisponible === 1 ? '' : 's'} disponible{saldo?.totalDisponible === 1 ? '' : 's'}
              </div>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{desgloseAbierto ? 'Ocultar detalle ▲' : 'Ver detalle ▼'}</span>
            </div>
            {saldo && saldo.adelantoPendiente > 0 && (
              <div style={{ fontSize: 13, color: 'var(--danger)', marginTop: 6 }}>
                Vacaciones adelantadas: -{saldo.adelantoPendiente} día{saldo.adelantoPendiente === 1 ? '' : 's'} (se descuentan de tu siguiente aniversario)
              </div>
            )}
            {desgloseAbierto && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {saldo && saldo.desglose.length > 0 ? (
                  saldo.desglose.map(d => (
                    <div key={d.allocationId} style={{ fontSize: 13, color: 'var(--body)' }}>
                      <strong>{d.diasDisponibles} días</strong> — {d.tipoNombre}, vence {fmtFechaDisplay(d.fechaFin)}
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--muted)' }}>Sin saldo disponible todavía</div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Nueva solicitud ───────────────────────────────────────────────── */}
      <div className="plat-card" style={{ maxWidth: 640, marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--heading)', marginBottom: 12 }}>Nueva solicitud</div>

        {esRH && (
          <div className="field">
            <label>Solicitar a nombre de (opcional)</label>
            <select
              value={usuarioObjetivoId}
              onChange={e => {
                setUsuarioObjetivoId(e.target.value);
                setCategoriaVisible('');
                if (!e.target.value) setAprobarInmediato(false);
              }}
              style={{ padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13.5, width: '100%' }}
            >
              <option value="">— Para mí mismo —</option>
              {(usuarios ?? [])
                .filter(u => u.id !== usuarioId)
                .map(u => (
                  // Prompt BJ-04 — formatUsuarioLabel(): puesto con asterisco si es_interno.
                  <option key={u.id} value={u.id}>{u.puesto ? `${u.nombre} (${formatUsuarioLabel(u)})` : u.nombre}</option>
                ))}
            </select>
          </div>
        )}

        {esParaOtro && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, marginBottom: 14 }}>
            <input type="checkbox" checked={aprobarInmediato} onChange={e => setAprobarInmediato(e.target.checked)} />
            Aprobar de inmediato (sin pasar por el aprobador habitual)
          </label>
        )}

        <div className="field">
          <label>Tipo de ausencia</label>
          <select
            value={categoriaVisible}
            onChange={e => setCategoriaVisible(e.target.value)}
            style={{ padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13.5, width: '100%' }}
          >
            <option value="">Selecciona…</option>
            {categoriasDisponibles.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {categoriasDisponibles.length === 0 && !cargandoSaldo && (
            <div className="ff-hint">
              {esParaOtro ? 'Este usuario no tiene ningún tipo de ausencia con saldo disponible' : 'No tienes ningún tipo de ausencia con saldo disponible'}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Input label="Desde" type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Input label="Hasta" type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} />
          </div>
        </div>

        {preview && (
          <div className="ff-hint" style={{ marginTop: -8, marginBottom: 10 }}>
            {preview.diasHabiles} día{preview.diasHabiles === 1 ? '' : 's'} hábil{preview.diasHabiles === 1 ? '' : 'es'}
            {preview.warning ? ` — ${preview.warning}` : ''}
          </div>
        )}

        <div className="field">
          <label>Motivo (opcional)</label>
          <textarea
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            rows={3}
            style={{ padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13.5, width: '100%', resize: 'vertical' }}
          />
        </div>

        <Button onClick={handleEnviar} disabled={crearMutation.isPending}>
          {crearMutation.isPending ? 'Enviando…' : esParaOtro && aprobarInmediato ? 'Crear y aprobar' : 'Enviar solicitud'}
        </Button>
      </div>

      {/* ── Historial ─────────────────────────────────────────────────────── */}
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--heading)', marginBottom: 10 }}>Historial de solicitudes</div>
      <div className="table-wrap">
        <div className="table-scroll">
          <table className="main-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Desde</th>
                <th>Hasta</th>
                <th>Días</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!historial || historial.length === 0 ? (
                <tr>
                  <td className="empty-td" colSpan={6}>
                    {cargandoHistorial ? 'Cargando…' : 'No hay solicitudes todavía'}
                  </td>
                </tr>
              ) : (
                historial.map(s => (
                  <tr key={s.id}>
                    <td>
                      {s.tipo_nombre}
                      {s.creado_por_id && s.creado_por_id !== s.usuario_id && (
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                          Creada por {s.creado_por_nombre} a nombre de {s.solicitante_nombre}
                        </div>
                      )}
                    </td>
                    <td>{fmtFechaDisplay(s.fecha_inicio_aprobada ?? s.fecha_inicio_solicitada)}</td>
                    <td>{fmtFechaDisplay(s.fecha_fin_aprobada ?? s.fecha_fin_solicitada)}</td>
                    <td>{s.dias_habiles_aprobados ?? s.dias_habiles_solicitados}</td>
                    <td>
                      <Badge tone={ESTADO_INFO[s.estado].tone}>{ESTADO_INFO[s.estado].label}</Badge>
                    </td>
                    <td>
                      {s.estado === 'pendiente' && (
                        <Button variant="secondary" size="sm" onClick={() => setACancelar(s)}>
                          Cancelar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        open={aCancelar !== null}
        title="¿Cancelar solicitud?"
        body="Esta acción no se puede deshacer."
        confirmLabel="Cancelar solicitud"
        danger
        onConfirm={() => aCancelar && cancelarMutation.mutate(aCancelar.id)}
        onCancel={() => setACancelar(null)}
      />

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
